<template>
  <div>
    <form v-if="loaded" @submit.prevent="preventDefaultSubmit()">
      <TheDetailHeader :title="$t(mainTitle)" :parent="parent">
        <template v-if="templateId" #badge>
          <BaseChip color="yellow" :text="$t('pages.survey.surveyCreatePage.labels.template')" />
        </template>
        <template #actions>
          <Button
            type="button"
            variant="default-primary"
            :disabled="checkDisabledButton"
            class="fixed bottom-0 left-0 w-screen rounded-none py-4 sm:static sm:w-auto sm:rounded-control sm:py-2"
            @click="save()"
          >
            {{ submitButtonText }}
          </Button>
          <ContestUpsell v-if="!hasModuleBySlug('game-contest')" />
        </template>
      </TheDetailHeader>
      <div v-if="!coverSizeOk" class="mx-6 mb-9 rounded-md bg-yellow-50 p-4 sm:mx-12">
        <div class="flex">
          <div class="shrink-0">
            <BaseIcon name="HeroInformationCircleIconSolid" class="w-5 text-yellow-400" />
          </div>
          <div class="ml-3 flex-1 md:flex md:justify-between">
            <p class="text-sm text-yellow-700">
              {{ $t('pages.contests.contestsCreatePage.infos.coverImageError') }}
            </p>
            <p v-if="currentTab !== 2" class="mt-3 text-sm md:ml-6 md:mt-0">
              <button
                type="button"
                class="whitespace-nowrap font-medium text-primary hover:text-primary/80"
                @click="currentTab = $t('pages.contests.contestsCreatePage.tabs.style')"
              >
                {{ $t('pages.contests.contestsCreatePage.buttons.backToStyleTab') }}
                <span aria-hidden="true">&rarr;</span>
              </button>
            </p>
          </div>
        </div>
      </div>
      <div v-if="!logoSizeOk" class="mb-9 rounded-md bg-yellow-50 p-4">
        <div class="flex">
          <div class="shrink-0">
            <BaseIcon name="HeroInformationCircleIconSolid" class="w-5 text-yellow-400" />
          </div>
          <div class="ml-3 flex-1 md:flex md:justify-between">
            <p class="text-sm text-yellow-700">
              {{ $t('pages.contests.contestsCreatePage.infos.logoError') }}
            </p>
            <p
              v-if="currentTab !== $t('pages.contests.contestsCreatePage.tabs.style')"
              class="mt-3 text-sm md:ml-6 md:mt-0"
            >
              <button
                type="button"
                class="whitespace-nowrap font-medium text-primary hover:text-primary/80"
                @click="currentTab = $t('pages.contests.contestsCreatePage.tabs.style')"
              >
                {{ $t('pages.contests.contestsCreatePage.buttons.backToStyleTab') }}
                <span aria-hidden="true">&rarr;</span>
              </button>
            </p>
          </div>
        </div>
      </div>
      <div class="px-6 pb-8 sm:px-12">
        <OrderedSteps
          v-show="!isShareViewVisible"
          :steps="steps"
          :current-step="currentTab"
          :has-step-number="true"
          @update:current-step="forwardSteps($event)"
        />
      </div>
      <div v-if="currentTab === 1" class="px-6 pb-6 sm:px-12 sm:pb-12">
        <ContestsTabAppearance :get-contest="contest" :selected-boost="selectedBoost" />
      </div>
      <div v-if="currentTab === 2" class="px-6 pb-6 sm:px-12 sm:pb-12">
        <ContestsTabReward :get-contest="contest" :is-edit-mode="isEditMode" />
      </div>
      <div v-if="currentTab === 3" class="px-6 pb-6 sm:px-12 sm:pb-12">
        <ContestsTabGlobal
          :categories-ids="categoriesIds"
          :is-edit-mode="isEditMode"
          :get-contest="contest"
          :is-template-mode="!!templateId"
          @selected-boost="emitSelectedBoost"
          @selected-frequency="emitSelectedFrequency"
        />
      </div>
      <div v-if="currentTab === 4" class="px-6 pb-6 sm:px-12 sm:pb-12">
        <ContestShareView :short-link="getShortLink" :file-name="fileName" :contact-url="contactUrl" />
      </div>
    </form>
    <TheLoadingPage v-else />
  </div>
</template>

<script>
import { Button } from '@/ui/button'
import useLocalStorage from '@/composables/useLocalStorage'
import dayjs from 'dayjs'
import { default as slugify } from 'slugify'

import OrderedSteps from '@/components/OrderedSteps.vue'
import TheLoadingPage from '@/components/layout/TheLoadingPage.vue'
import useAccess from '@/composables/access'
import useColor from '@/composables/color'
import useEvent from '@/composables/event'

import useMedia from '@/composables/media'
import useSessionStorage from '@/composables/sessionStorage'
import useStore from '@/composables/store'
import ContestUpsell from './Components/ContestUpsell.vue'
import ContestsTabAppearance from './Components/ContestsTabAppearance.vue'
import ContestsTabGlobal from './Components/ContestsTabGlobal.vue'
import ContestsTabReward from './Components/ContestsTabReward.vue'
import ContestShareView from './Components/ContestShareView.vue'
import { calculateTotalProbability, capitalizeFirstLetter, isUrlValid, withEveryDaySlotProbability } from '../utils'

const boostBaseTranslation = 'pages.contests.contestsCreatePage.global.form.fields.boost'
const boosts = [
  {
    id: 'none',
    title: `${boostBaseTranslation}.none.title`,
    description: `${boostBaseTranslation}.none.description`
  },
  {
    id: 'custom',
    title: `${boostBaseTranslation}.custom.title`,
    description: `${boostBaseTranslation}.custom.description`
  },
  {
    id: 'facebook',
    title: `${boostBaseTranslation}.facebook.title`,
    description: `${boostBaseTranslation}.facebook.description`
  },
  {
    id: 'instagram',
    title: `${boostBaseTranslation}.instagram.title`,
    description: `${boostBaseTranslation}.instagram.description`
  },
  {
    id: 'google',
    title: `${boostBaseTranslation}.google.title`,
    description: `${boostBaseTranslation}.google.description`
  }
]

export default {
  components: {
    TheLoadingPage,
    Button,
    ContestsTabGlobal,
    ContestsTabReward,
    ContestsTabAppearance,
    ContestUpsell,
    OrderedSteps,
    ContestShareView
  },
  setup() {
    const selectedBoost = ref(boosts[0])
    const selectedPlayableFrequency = ref()
    const { send } = useEvent()
    const { getContrast } = useColor()
    const { merchantName, user, merchantLogo } = useLocalStorage()
    const { duplicateContestItem, isEmptyDuplicateContestItem } = useSessionStorage()
    const { getStoreById, getStoresByPermission, getStoreSchedulingPlatformUrl, selectedStore } = useStore()
    const { hasModuleBySlug } = useAccess()
    const { isDefaultLogo } = useMedia()

    const route = useRoute()

    const notification = useNotification()
    const { getBase64 } = useMedia()

    const emitSelectedBoost = (item) => {
      selectedBoost.value = item
    }

    const emitSelectedFrequency = (item) => {
      selectedPlayableFrequency.value = item
    }

    return {
      boosts,
      selectedBoost,
      emitSelectedBoost,
      selectedPlayableFrequency,
      emitSelectedFrequency,
      send,
      getContrast,
      merchantName,
      merchantLogo,
      duplicateContestItem,
      isEmptyDuplicateContestItem,
      user,
      getStoreById,
      getStoresByPermission,
      route,
      notification,
      getBase64,
      hasModuleBySlug,
      isDefaultLogo,
      getStoreSchedulingPlatformUrl,
      selectedStore
    }
  },
  data() {
    return {
      defaultLogo: null,
      steps: [
        {
          id: 'style',
          title: this.$t('pages.contests.contestsCreatePage.tabs.style'),
          isComplete: false,
          href: '#',
          stepOrder: 1
        },
        {
          id: 'prices',
          title: this.$t('pages.contests.contestsCreatePage.tabs.prices'),
          isComplete: false,
          href: '#',
          stepOrder: 2
        },
        {
          id: 'global',
          title: this.$t('pages.contests.contestsCreatePage.tabs.global'),
          isComplete: false,
          href: '#',
          stepOrder: 3
        }
      ],
      maximumRewardsCount: 8,
      currentTab: 1,
      loaded: false,
      contest: {
        name: '',
        slug: '',
        isVisibleInWebapp: !!this.$route.query?.activeInWebApp,
        isOtpRequired: false,
        fromDate: null,
        toDate: null,
        coverImage: null,
        logo: null,
        title: this.$t('pages.contests.contestsCreatePage.preview.title'),
        subtitle: this.$t('pages.contests.contestsCreatePage.preview.subtitle'),
        askActionTitle: this.$t('pages.contests.contestsCreatePage.preview.askActionTitle.custom'),
        askActionText: this.$t('pages.contests.contestsCreatePage.preview.askActionText'),
        askActionButtonText: this.$t('pages.contests.contestsCreatePage.preview.askActionButtonText'),
        canLose: false,
        losingProbability: null,
        losingTileColor: '#BFBFBF',
        losingTileText: this.$t('pages.contests.contestsCreatePage.preview.losingTileText'),
        losingText: this.$t('pages.contests.contestsCreatePage.preview.losingText'),
        winningText: this.$t('pages.contests.contestsCreatePage.preview.winningText'),
        callToActionText: this.$t('pages.contests.contestsCreatePage.preview.callToActionText'),
        formTitle: this.$t('pages.contests.contestsCreatePage.preview.formTitle'),
        formSubtitle: this.$t('pages.contests.contestsCreatePage.preview.formSubtitle'),
        formSubmitButtonText: this.$t('pages.contests.contestsCreatePage.preview.formSubmitButtonText'),
        optinText: this.$t('pages.contests.contestsCreatePage.preview.optinText', { merchantName: this.merchantName }),
        contestGameRewards: [
          {
            name: this.$t('pages.contests.contestsCreatePage.prices.rewardNamePlaceholder', { index: '1' }),
            points: 0,
            hasQrcode: false,
            probability: 0.1,
            color: '#BFBFBF',
            textColor: '#000000',
            notificationTextOverride: null,
            rewardTypeSlug: 'points'
          },
          {
            name: this.$t('pages.contests.contestsCreatePage.prices.rewardNamePlaceholder', { index: '2' }),
            points: 0,
            hasQrcode: true,
            probability: 25,
            color: '#BFBFBF',
            textColor: '#000000',
            notificationTextOverride: null,
            rewardTypeSlug: 'free_product',
            rewardTypeId: null,
            externalId: null,
            value: null,
            validityInDays: null,
            minimumOrderAmount: null
          },
          {
            name: this.$t('pages.contests.contestsCreatePage.prices.rewardNamePlaceholder', { index: '3' }),
            points: 0,
            hasQrcode: true,
            probability: 25,
            color: '#BFBFBF',
            textColor: '#000000',
            notificationTextOverride: null,
            rewardTypeSlug: 'free_product',
            rewardTypeId: null,
            externalId: null,
            value: null,
            validityInDays: null,
            minimumOrderAmount: null
          },
          {
            name: this.$t('pages.contests.contestsCreatePage.prices.rewardNamePlaceholder', { index: '4' }),
            points: 0,
            hasQrcode: true,
            probability: 49.9,
            color: '#BFBFBF',
            textColor: '#000000',
            notificationTextOverride: null,
            rewardTypeSlug: 'free_product',
            rewardTypeId: null,
            externalId: null,
            value: null,
            validityInDays: null,
            minimumOrderAmount: null
          }
        ],
        isPlayableManyTimes: false,
        daysBetweenParticipations: 0,
        isClickRequiredToPlay: false,
        messageContentPoints: '',
        messageTemplate: {
          content: '',
          subject: this.$t('pages.contests.contestsCreatePage.preview.messageTemplate.subject'),
          messageChannelId: 1
        },
        messageTemplateQrcode: {
          content: '',
          messageChannelId: 1
        },
        stores: [],
        losingNotificationText: this.$t(
          'pages.contests.contestsCreatePage.preview.messageTemplate.losingNotificationText'
        ),
        sendingNotificationText: this.$t(
          'pages.contests.contestsCreatePage.preview.messageTemplate.sendingNotificationText.sms'
        ),
        waitingValidationText: this.$t(
          'pages.contests.contestsCreatePage.preview.messageTemplate.waitingValidationText'
        ),
        targetUrl: null
      },
      categoriesIds: {
        facebookId: null,
        googleId: null,
        instagramId: null
      },
      targetUrlType: 'store',
      sumPercentage: 0,
      wheelGiftId: null,
      hasStoresAvailable: true,
      hasStoresDisabledByBoost: false,
      coverSizeOk: true,
      logoSizeOk: true,
      invalidCharacters: {
        reward: false,
        sms: false
      },
      error: null
    }
  },
  computed: {
    parent() {
      return this.isEditMode
        ? { label: this.contest.name, to: { name: 'LegacyContestDetail', params: { contestId: this.contest.id } } }
        : { label: this.$t('pages.contests.contestsPage.title'), to: { name: 'ContestList' } }
    },
    submitButtonText() {
      if (this.currentTab === 4) {
        return this.$t('pages.contests.sharePage.finishButton')
      }
      return this.$t(
        !this.isEditMode
          ? 'pages.contests.contestsCreatePage.buttons.createContestGame'
          : 'pages.contests.contestsCreatePage.buttons.update'
      )
    },
    computedWheelRewards() {
      const wheelRewards = []
      this.contest.contestGameRewards.forEach((reward, index) => {
        wheelRewards.push({
          id: index + 1,
          bgColor: reward.color,
          color: reward.textColor,
          value: reward.name,
          probability: reward.probability,
          hasQrcode: reward.hasQrcode
        })
      })
      if (this.contest.canLose) {
        wheelRewards.push({
          id: wheelRewards.length + 1,
          bgColor: this.contest.losingTileColor,
          color: this.contest.losingTileTextColor,
          value: this.contest.losingTileText,
          probability: this.contest.losingProbability,
          hasQrcode: false
        })
      }
      return wheelRewards
    },
    rewardNameMaximumCharacters() {
      if (this.computedWheelRewards.length >= 7) {
        return 20
      } else if (this.computedWheelRewards.length === 6) {
        return 24
      } else {
        return 30
      }
    },
    maximumWinPercentage() {
      return 100 - (this.contest.canLose ? this.contest.losingProbability : 0)
    },
    merchantId() {
      return this.user.merchant.id
    },
    isEditMode() {
      return this.route.params?.contestId > 0
    },
    contestSlug() {
      const merchantNameAsSlug = slugify(this.merchantName, {
        lower: true,
        strict: true,
        locale: 'fr'
      })
      const contestSlug = slugify(this.contest.name, {
        lower: true,
        strict: true,
        locale: 'fr'
      })
      return `${merchantNameAsSlug}-${contestSlug}`
    },
    countStores() {
      return this.getStoresByPermission('contest-game.create').length
    },
    checkDisabledButton() {
      let checkRewards = []
      this.checkAllowedCharacters(this.contest.messageTemplate.content, 'message')
      this.contest.contestGameRewards.forEach((reward) => {
        checkRewards.push(/[^\p{L}\p{N}\p{P}\p{Z}\p{M}^$€<>=+¨~−\n]/gu.test(reward.notificationTextOverride))
      })
      const contestIsInvalid = checkRewards.includes(true) || this.invalidCharacters.message || !this.checkSumPercentage
      return contestIsInvalid || !this.hasModuleBySlug('game-contest')
    },
    checkSumPercentage() {
      this.calculateSumPercentage()
      return this.isEditMode ? this.sumPercentage === 100 : true
    },
    textColor() {
      return `color: ${this.contest.textColor}`
    },
    isSms() {
      return this.channels.filter((c) => c.id === this.contest.messageTemplate.messageChannelId)[0].slug === 'sms'
    },
    isMail() {
      return this.channels.filter((c) => c.id === this.contest.messageTemplate.messageChannelId)[0].slug === 'mail'
    },
    mainTitle() {
      if (this.isEditMode) {
        return 'pages.contests.contestsEditPage.title'
      }

      return this.templateId
        ? 'pages.contests.contestsCreatePage.titleWithTemplate'
        : 'pages.contests.contestsCreatePage.title'
    },

    templateId() {
      return this.route.query?.templateId
    },
    fileName() {
      const isSingleStore = this.selectedStore !== 0
      const name = `${this.contest.slug}${isSingleStore ? `-${this.getStoreById(this.selectedStore).slug}` : ''}`

      return `pongo-contest-${name}.png`
    },
    getShortLink() {
      if (this.contest.stores.length === 1) {
        return this.contest?.storesShortLinks ? this.contest.storesShortLinks[0]?.shortLink?.shortLink : ''
      }
      return this.selectedStore === 0
        ? this.contest.shortLink?.shortLink
        : this.contest.stores.filter((store) => store.storeId === this.selectedStore)[0]?.shortLink?.shortLink
            ?.shortLink
    },
    contactUrl() {
      const url = `${this.getStoreSchedulingPlatformUrl(
        this.selectedStore
      )}?utm_source=pongo&utm_medium=dashboard&utm_campaign=dashboard&utm_content=demo`
      return url
    },
    isShareViewVisible() {
      return this.$route.params?.success === 'success'
    }
  },
  watch: {
    selectedBoost() {
      this.contest.isClickRequiredToPlay = this.selectedBoost?.id !== 'none'
      if (['none', 'custom'].includes(this.selectedBoost?.id)) {
        return true
      }
      if (this.selectedBoost?.id !== 'custom') {
        this.contest.targetUrl = null
      }
    },
    selectedPlayableOnceADay() {
      this.contest.isPlayableOnceADay = this.selectedPlayableOnceADay.id === 'everyday'
    }
  },
  async created() {
    if (this.isShareViewVisible) {
      this.currentTab = 4
    }
    // Edit an existing contest
    if (this.isEditMode) {
      this.steps.push({
        id: 'launch',
        title: this.$t('pages.contests.contestsCreatePage.tabs.launch'),
        isComplete: false,
        href: '#',
        icon: null,
        stepOrder: 4
      })
      this.axios.get(`/contest-games/${this.route.params.contestId}`).then(async (response) => {
        this.generateContest(response.data.data)
        await this.fetchCategoriesUrl()
        await this.fetchData(false)
        this.loaded = true
      })
    } else {
      // Create a new contest
      await this.fetchDefaultImages()
      await this.fetchData(true)

      if (this.templateId) {
        await this.fetchTemplate()
      }

      // Duplicate a contest
      if (!this.isEmptyDuplicateContestItem) {
        this.generateContest(this.duplicateContestItem)

        this.findSelectedBoost()
      }
      await this.generateImg()

      this.loaded = true
    }
  },
  unmounted() {
    this.duplicateContestItem = {}
  },
  methods: {
    preventDefaultSubmit(e) {
      e?.preventDefault()
    },
    forwardSteps(e) {
      if (this.steps[e - 1]?.isComplete) {
        this.steps[e - 1].isComplete = false
      }
      this.checkContestSettings(e)
    },
    checkAllSteps(step) {
      if (step !== 4) {
        return
      }
      this.steps.forEach((step) => (step.isComplete = true))
      this.steps[3].isComplete = true
    },
    generateContest(contest) {
      this.contest = contest
      if (this.isEditMode) {
        this.contest.storesShortLinks = this.contest.stores
      }

      const stores = []
      this.contest.stores.forEach((store) => {
        stores.push(Number(store.storeId))
      })
      this.contest.stores = stores

      this.createMessageChannelId()
      this.formatDate()
      this.generateLosingProbability()
      this.findSelectedBoost()
    },
    async generateImg() {
      try {
        if (this.contest.coverImageUrl) {
          this.contest.coverImage = await this.getBase64(this.contest.coverImageUrl)
        }
        if (this.contest?.logoUrl) {
          this.contest.logo = await this.getBase64(this.contest.logoUrl)
          return
        }
        if (this.merchantLogo) {
          this.defaultLogo = await this.getBase64(this.merchantLogo)
          this.contest.logo = this.defaultLogo
        }
      } catch {
        this.loaded = true
      }
    },
    createMessageChannelId() {
      this.contest.messageTemplate.messageChannelId = this.contest.messageTemplate.messageChannel.id
      if (this.contest.messageTemplateQrcode !== null) {
        this.contest.messageTemplateQrcode.messageChannelId = this.contest.messageTemplate.messageChannelId
      } else {
        this.contest.messageTemplateQrcode = {
          content: this.contest.messageTemplate.content,
          messageChannelId: this.contest.messageTemplate.messageChannelId
        }
      }
    },
    formatDate() {
      const dates = [
        dayjs(new Date(this.contest.fromDate)).format('DD/MM/YYYY'),
        dayjs(new Date(this.contest.toDate)).format('DD/MM/YYYY')
      ]
      this.dateRangeValue = dates
      this.dateRangeChanged(dates)
    },
    generateLosingProbability() {
      this.contest.losingProbability = 100
      this.contest.contestGameRewards.forEach((reward) => {
        this.contest.losingProbability -= reward.probability
      })
      this.contest.losingProbability = Math.round(this.contest.losingProbability * 100) / 100

      this.contest.canLose = this.contest.losingProbability > 0
      if (!this.contest.canLose) {
        this.contest.losingProbability = 25
      }
    },
    findSelectedBoost() {
      this.selectedBoost = boosts.find((boost) => boost.id === this.contest.contestGameBoostType.slug)
      this.contest.isClickRequiredToPlay = this.selectedBoost.id !== 'none'
    },
    generateWheelGiftId() {
      let random = Math.random() * 100
      let total = 0
      let lastIndex = this.computedWheelRewards.length

      for (let i = 0; i < lastIndex; ++i) {
        total += Number(this.computedWheelRewards[i].probability)
        if (random <= total) {
          this.wheelGiftId = this.computedWheelRewards[i].id
          return
        }
      }
    },
    hasGoogle(store) {
      return store?.urls?.map((url) => url.storeUrlCategoryId === this.categoriesIds.googleId)?.includes(true)
    },
    hasFacebook(store) {
      return store?.urls?.map((url) => url.storeUrlCategoryId === this.categoriesIds.facebookId)?.includes(true)
    },
    hasInstagram(store) {
      return store?.urls?.map((url) => url.storeUrlCategoryId === this.categoriesIds.instagramId)?.includes(true)
    },
    isStoreAvailable(storeId) {
      if (['none', 'custom'].includes(this.selectedBoost.id)) {
        return true
      }

      const store = this.getStoreById(storeId)
      if (this.selectedBoost.id === 'facebook' && !this.hasFacebook(store)) {
        return false
      }
      if (this.selectedBoost.id === 'instagram' && !this.hasInstagram(store)) {
        return false
      }
      if (this.selectedBoost.id === 'google' && !this.hasGoogle(store)) {
        return false
      }
      return true
    },
    async fetchCategoriesUrl() {
      return this.axios.get('/store-url-categories').then((response) => {
        const categories = response.data.data
        this.categoriesIds.facebookId = categories.filter((category) => category.slug === 'facebook')[0].id
        this.categoriesIds.googleId = categories.filter((category) => category.slug === 'google')[0].id
        this.categoriesIds.instagramId = categories.filter((category) => category.slug === 'instagram')[0].id
      })
    },
    async fetchDefaultImages() {
      return this.axios.get(`/merchants/${this.merchantId}/media`).then((response) => {
        this.contest.defaultCover = response.data.data.coverA
      })
    },
    async fetchData(withCategoriesUrl = false) {
      const { data } = await this.axios.get('/message-channels')
      this.channels = data.data

      const { data: dataMerchant } = await this.axios.get(`/merchants/${this.merchantId}/colors`)
      if (!this.isEditMode && this.isEmptyDuplicateContestItem) {
        const colors = dataMerchant.data
        this.contest.backgroundColor = this.contest.backgroundColor ?? colors.background
        this.contest.buttonColor = this.getContrast(this.contest.backgroundColor) ?? this.getContrast(colors.background)
        this.contest.textColor = this.getContrast(this.contest.backgroundColor) ?? this.getContrast(colors.background)
        this.contest.buttonTextColor = this.contest.backgroundColor ?? colors.background
        this.contest.losingTileTextColor = this.contest.losingTileTextColor ?? colors.text
      }

      if (withCategoriesUrl) {
        await this.fetchCategoriesUrl()
      }
    },
    setEqualProbabilities() {
      const probability = Number.parseFloat(this.maximumWinPercentage / this.contest.contestGameRewards.length).toFixed(
        2
      )
      this.contest.contestGameRewards = this.contest.contestGameRewards.map((reward) => {
        reward.probability = probability
        return reward
      })
      this.calculateSumPercentage()
      // For example, for 3 rewards with 33.33%, we add +0.01 to the first one to get 33.34% / 33.33% / 33.33%
      if (this.sumPercentage !== 100) {
        const diff = (100 - this.sumPercentage) * 100
        this.contest.contestGameRewards[0].probability = Number(
          (this.contest.contestGameRewards[0].probability * 100 + diff) / 100
        ).toFixed(4)
        this.calculateSumPercentage()
      }
    },
    calculateSumPercentage() {
      this.sumPercentage = calculateTotalProbability(this.computedWheelRewards)
    },
    canLose(canLose) {
      this.contest.canLose = canLose
      this.setEqualProbabilities()
    },
    dateRangeChanged(newDateRangeValue) {
      this.contest.fromDate = newDateRangeValue[0] ?? null
      this.contest.toDate = newDateRangeValue[1] ?? null
    },
    requiredToPlay(action) {
      this.contest.isClickRequiredToPlay = action
    },
    checkAllowedCharacters(value, element) {
      this.invalidCharacters[element] = /[^\p{L}\p{N}\p{P}\p{Z}\p{M}^$€<>=+¨~−\n]/gu.test(value)
    },
    isInt(value) {
      return !isNaN(value) && parseInt(Number(value)) == value && !isNaN(parseInt(value, 10))
    },
    removeAllNotificationTextOverride() {
      this.contest.contestGameRewards.forEach((reward) => (reward.notificationTextOverride = null))
    },
    checkErrors(currentStep) {
      this.error = null
      this.checkContestSettings(currentStep)
      this.checkEmptyFields()
      this.checkCanLoseErrors()
      this.checkMessageTemplate()
      this.checkRewardsError()
      this.checkCustomizableFrequency()
      this.checkGlobalSettings()
    },
    checkCustomizableFrequency() {
      if (this.selectedPlayableFrequency?.id === 'customize' && this.contest.daysBetweenParticipations < 2) {
        {
          this.error = this.$t('pages.contests.contestsCreatePage.notifications.error.customizeDays')
          return
        }
      }
    },
    checkEmptyFields() {
      const fieldsToCheck = [
        'name',
        'stores',
        'title',
        'subtitle',
        'callToActionText',
        'askActionTitle',
        'askActionText',
        'askActionButtonText',
        'waitingValidationText',
        'winningText',
        'losingText',
        'losingTileText',
        'sendingNotificationText',
        'optinText',
        'formTitle',
        'formSubtitle',
        'formSubmitButtonText'
      ]
      fieldsToCheck.forEach((field) => {
        if (this.contest[field].length === 0) {
          this.error = this.$t(`pages.contests.contestsCreatePage.notifications.error.${field}`)
          return
        }
      })
    },
    checkCanLoseErrors() {
      if (!this.contest.canLose) {
        return
      }

      const fieldsToCheck = ['losingProbability', 'losingTileText', 'losingText']
      fieldsToCheck.forEach((field) => {
        if (this.contest[field]?.length === 0 || !this.contest[field]) {
          this.error = this.$t(`pages.contests.contestsCreatePage.notifications.error.${field}`)
          return
        }
      })
    },
    checkMessageTemplate() {
      if (this.contest.messageTemplate.messageChannelId === null) {
        this.$t('pages.contests.contestsCreatePage.notifications.error.communicationChannel')
      } else if (this.contest.messageTemplate.content?.length === 0) {
        this.$t('pages.contests.contestsCreatePage.notifications.error.messageTemplate')
      } else if (this.isSms && this.contest.messageTemplate.content?.length > 145) {
        this.$t('pages.contests.contestsCreatePage.notifications.error.messageTemplateLength', { nb: 145 })
      } else if (this.isMail && this.contest.messageTemplate.subject?.length === 0) {
        this.$t('pages.contests.contestsCreatePage.notifications.error.messageTemplateMailObject')
      }
    },
    checkMessageChannelRule(rewardMessage) {
      const rewardMessageIsTooLong = rewardMessage && rewardMessage.length > 160

      if (this.contest.messageTemplate.messageChannelId === 1 && rewardMessageIsTooLong) {
        this.notification.create('error', {
          text: this.$t('pages.contests.contestsCreatePage.notifications.error.rewardNotificationLength'),
          timeout: 3000
        })
        return true
      }
      return false
    },
    checkRewardsError() {
      const rewards = this.contest.contestGameRewards
      const contestGameRewardsLength = rewards.length

      const showErrorNotification = (errorKey, params) => {
        this.error = this.$t(`pages.contests.contestsCreatePage.notifications.error.${errorKey}`, params)
        this.notification.create('error', { text: this.error })
      }

      if (contestGameRewardsLength < 2 || contestGameRewardsLength > this.maximumRewardsCount) {
        showErrorNotification('maximumRewardsCount', { from: 2, to: this.maximumRewardsCount })
        return false
      }

      const hasEmptyName = rewards.some((reward) => reward.name.length === 0)
      if (hasEmptyName) {
        showErrorNotification('priceName')
        return false
      }

      const hasInvalidPoints = rewards.some((reward) => reward.points !== null && !this.isInt(reward.points))
      if (hasInvalidPoints) {
        showErrorNotification('rewardPointsNumber')
        return false
      }

      const hasLongRewardName = rewards.some((reward) => reward.name.length > this.rewardNameMaximumCharacters)
      if (hasLongRewardName) {
        showErrorNotification('rewardNameLength', { nb: this.rewardNameMaximumCharacters })
        return false
      }

      const hasNoProbability = rewards.some((reward) => !reward.probability)
      if (hasNoProbability) {
        showErrorNotification('priceProbability')
        return false
      }

      if (this.sumPercentage !== 100) {
        showErrorNotification('probabilityNot100')
        return false
      }

      const hasInvalidText = rewards.some((reward) => this.checkMessageChannelRule(reward.notificationTextOverride))
      if (hasInvalidText) {
        showErrorNotification('rewardNotificationLength', { nb: 160 })
        return false
      }

      return true
    },

    checkGlobalSettings() {
      if (this.contest.conditionsUrl && !isUrlValid(this.contest.conditionsUrl)) {
        this.error = this.$t('pages.contests.contestsCreatePage.notifications.error.conditionsUrl')
        return false
      }

      if (this.selectedBoost?.id === 'custom') {
        if (!this.contest.targetUrl) {
          this.error = this.$t('pages.contests.contestsCreatePage.notifications.error.customLink')
          return false
        }

        if (!isUrlValid(this.contest.targetUrl)) {
          this.error = this.$t('pages.contests.contestsCreatePage.notifications.error.targetUrl')
          return false
        }
      }

      // Check if all selected stores the user has access to are available with the selected boost

      if (this.selectedBoost?.id && !['none', 'custom'].includes(this.selectedBoost.id)) {
        const permittedStoreIds = this.getStoresByPermission('contest-game.create').map((s) => s.id)
        const unavailableStores = this.contest.stores.filter((storeId) => {
          return permittedStoreIds.includes(storeId) && !this.isStoreAvailable(storeId)
        })

        if (unavailableStores.length > 0) {
          this.error = this.$t('pages.contests.contestsCreatePage.global.form.infos.selectedStoresNotAvailable', {
            count: unavailableStores.length,
            boostType: capitalizeFirstLetter(this.selectedBoost.id)
          })
          return false
        }
      }

      return true
    },

    checkContestSettings(step) {
      const customBoosterWithoutLink = this.selectedBoost?.id === 'custom' && this.contest.targetUrl === null
      let stepIsComplete
      switch (this.currentTab) {
        case 1:
          if (this.isDefaultLogo(this.merchantLogo) && !this.isEditMode && this.defaultLogo === this.contest.logo) {
            this.notification.create('error', {
              text: this.$t('pages.contests.contestsCreatePage.notifications.error.logo')
            })
            return
          } else {
            this.steps[0].isComplete = true
            this.currentTab = step
          }
          return
        case 2:
          stepIsComplete = this.checkRewardsError()
          if (stepIsComplete) {
            this.steps[1].isComplete = true
            this.currentTab = step
          }
          return
        case 3:
          if (customBoosterWithoutLink) {
            this.error = this.$t('pages.contests.contestsCreatePage.notifications.error.customLink')
          } else {
            this.currentTab = step
          }
          break
        case 4:
          this.currentTab = step
      }
    },
    save() {
      if (this.isShareViewVisible) {
        this.$router.push({
          name: 'LegacyContestDetail',
          params: { contestId: this.contest.id }
        })
        return
      }
      this.checkErrors(this.currentTab)

      if (this.contest.fromDate === null && this.contest.toDate === null) {
        this.contest.fromDate = dayjs().format('DD/MM/YYYY')
        this.contest.toDate = '01/01/3000'
      }

      if (this.error !== null) {
        this.notification.create('error', {
          text: this.error,
          timeout: 5000
        })

        return
      }

      if (this.templateId) {
        this.send('segment', 'contest.template:create:clicked', {
          templateId: Number(this.templateId),
          contestId: this.user.merchant.publicId,
          module: 'contest'
        })
      }

      const stores = this.contest.stores.map((storeId) => {
        const formattedStoreId = {
          storeId: storeId
        }
        if (['facebook', 'instagram', 'google'].includes(this.selectedBoost.id)) {
          const store = this.getStoreById(storeId)

          const cateogryId = `${this.selectedBoost.id}Id`
          const selectedBoostCategoryId = this.categoriesIds[cateogryId]

          return {
            ...formattedStoreId,
            storeUrlId: store?.urls?.filter((url) => url.storeUrlCategoryId === selectedBoostCategoryId)[0]?.id
          }
        }

        return formattedStoreId
      })

      if (stores.length === 0) {
        this.notification.create('error', {
          text: this.$t('pages.contests.contestsCreatePage.notifications.error.stores'),
          timeout: 5000
        })
        return
      }

      if (this.contest.isGeneric) {
        this.removeAllNotificationTextOverride()
      }

      const params = {
        name: this.contest.name,
        slug: this.contestSlug,
        conditionsUrl: this.contest.conditionsUrl,
        title: this.contest.title,
        subtitle: this.contest.subtitle,
        callToActionText: this.contest.callToActionText,
        askActionTitle: this.contest.askActionTitle,
        askActionText: this.contest.askActionText,
        askActionButtonText: this.contest.askActionButtonText,
        waitingValidationText: this.contest.waitingValidationText,
        winningText: this.contest.winningText,
        losingText: this.contest.losingText,
        losingNotificationText: this.contest.losingNotificationText,
        losingTileColor: this.contest.losingTileColor,
        losingTileTextColor: this.contest.losingTileTextColor,
        losingTileText: this.contest.losingTileText,
        sendingNotificationText: this.contest.sendingNotificationText,
        optinText: this.contest.optinText,
        formTitle: this.contest.formTitle,
        formSubtitle: this.contest.formSubtitle,
        formSubmitButtonText: this.contest.formSubmitButtonText,
        backgroundColor: this.contest.backgroundColor,
        textColor: this.contest.textColor,
        buttonColor: this.contest.buttonColor,
        buttonTextColor: this.contest.buttonTextColor,
        coverImage: this.contest.coverImage,
        logo: this.contest.logo,
        stores: stores,
        contestGameRewards: this.contest.contestGameRewards.map(withEveryDaySlotProbability),
        contestGameBoostType: this.selectedBoost.id,
        messageTemplate: this.contest.messageTemplate,
        messageTemplateQrcode: this.contest.messageTemplateQrcode,
        isPlayableManyTimes: this.contest.isPlayableManyTimes,
        daysBetweenParticipations: parseInt(this.contest.daysBetweenParticipations, 10),
        isClickRequiredToPlay: this.contest.isClickRequiredToPlay,
        targetUrl: this.contest.targetUrl,
        fromDate: dayjs(this.contest.fromDate, 'DD/MM/YYYY').format('YYYY-MM-DD'),
        toDate: dayjs(this.contest.toDate, 'DD/MM/YYYY').format('YYYY-MM-DD'),
        isEnabled: true,
        isVisibleInWebapp: this.contest.isVisibleInWebapp,
        isOtpRequired: this.contest.isOtpRequired ?? false,
        mechanic: this.contest.mechanic,
        isLegacy: true
      }
      this.loaded = false

      if (!this.isEditMode) {
        this.axios
          .post('/contest-games', params)
          .then((response) => {
            this.notification.create('success', {
              text: this.$t('pages.contests.contestsCreatePage.notifications.create.success')
            })
            this.contest = response.data.data
            this.$router.push({
              name: this.contest?.isLegacy ? 'LegacyContestEdit' : 'ContestEdit',
              params: { contestId: this.contest.id, success: 'success' }
            })
          })
          .catch((e) => {
            const textError =
              e.response.data?.errors?.slug !== null
                ? Object.values(e.response.data.errors)[0][0]
                : this.$t('pages.contests.contestsCreatePage.notifications.create.error')

            this.notification.create('error', {
              text: textError,
              persist: true
            })
          })
          .finally(() => (this.loaded = true))
      } else {
        this.axios
          .put(`/contest-games/${this.route.params.contestId}`, params)
          .then((response) => {
            this.notification.create('success', {
              text: this.$t('pages.contests.contestsEditPage.notifications.create.success')
            })
            const contestId = response.data.data.id
            this.$router.push({
              name: 'LegacyContestDetail',
              params: { contestId: contestId }
            })
          })
          .catch((e) => {
            const textError =
              e.response.data.errors.slug !== null
                ? Object.values(e.response.data.errors)[0][0]
                : this.$t('pages.contests.contestsEditPage.notifications.create.error')

            this.notification.create('error', {
              text: textError,
              persist: true
            })
          })
          .finally(() => (this.loaded = true))
      }
    },
    async fetchTemplate() {
      const { data } = await this.axios.get(`templates/${this.templateId}?module=contests`)
      const templatableData = data.data.templatable
      this.contest = {
        ...this.contest,
        ...templatableData
      }
      const contestGameRewards = templatableData.contestGameRewards.map((reward) => ({
        ...reward,
        rewardTypeSlug: reward.rewardType.slug
      }))
      this.contest.contestGameRewards = contestGameRewards
      this.contest.contestGameBoostType = {
        slug: templatableData.contestGameBoostType.slug
      }
      this.contest.askActionTitle = this.$t(
        `pages.contests.contestsCreatePage.preview.askActionTitle.${this.contest.contestGameBoostType.slug}`
      )

      this.generateLosingProbability()
      this.findSelectedBoost()
    }
  }
}
</script>

<style>
.wheel textPath {
  font-family: 'Inter var', sans-serif;
  font-weight: bold;
}
</style>
